import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { useSelector } from "react-redux";
import styles from "../../../../../src/pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import CustomToggle from "../../../../components/CustomToggle/CustomToggle";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import useLessonPlanData from "./useLessonPlanData";
import { buildLessonPlanColumns } from "./lessonPlanColumns";
import LessonPlanHistoryPanel from "./LessonPlanHistoryPanel";

function SubmittedLessonPlan() {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};
  const user = TokenService.getUser();
  const roleId = Number(user?.role?.id);
  const hideActions = [4, 6].includes(roleId);

  // initial toggle = "all" (per requirement)
  const [weekFilter, setWeekFilter] = useState("all");
  const [selectedRow, setSelectedRow] = useState(null);

  // ─── PopupModal state ───
  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    title: "",
    message: "",
    onOk: null,
    showCancel: false,
  });

  const closeModal = () =>
    setModalState({
      open: false,
      type: "",
      title: "",
      message: "",
      onOk: null,
      showCancel: false,
    });

  const {
    data,
    loading,
    rowCount,
    pagination,
    setPagination,
    columnFilters,
    handleColumnFiltersChange,
    topicOptions,
    subTopicOptions,
    refetch,
  } = useLessonPlanData(1, weekFilter === "this_week" ? 1 : 0);

  const handleView = (row) => {
    navigate("/user/lessonplanview", {
      state: { ID: row.def_id, data: row },
    });
  };

  const handleSelectRow = (rowData) => {
    if (selectedRow && selectedRow.id === rowData.id) {
      setSelectedRow(null);
    } else {
      setSelectedRow(rowData);
    }
  };

  // Step 1: click DELETE → show confirmation modal
  const handleDelete = () => {
    if (!selectedRow) {
      setModalState({
        open: true,
        type: "warning",
        title: t.warning || "Warning",
        message: t.selectRowToDelete || "Please select a row to delete",
        onOk: closeModal,
        showCancel: false,
      });
      return;
    }
    setModalState({
      open: true,
      type: "warning",
      title: t.confirmDelete || "Confirm Delete",
      message:
        t.confirmDeleteMessage ||
        "Are you sure you want to delete this lesson plan?",
      onOk: doDelete,
      showCancel: true,
    });
  };

  // Step 2: user confirmed → call API
  const doDelete = async () => {
    closeModal();
    if (!selectedRow) return;

    try {
      const response = await AxiosInstance.delete(`/book/lessonPlan/delete`, {
        id: selectedRow.id,
        def_id: selectedRow.def_id,
        item_id: selectedRow.item_id,
      });
      if (response?.data?.status) {
        setSelectedRow(null);
        setModalState({
          open: true,
          type: "success",
          title: t.success || "Success",
          message:
            response.data.message || t.deleteSuccess || "Deleted successfully",
          onOk: () => {
            closeModal();
            refetch();
          },
          showCancel: false,
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          title: t.failed || "Failed",
          message: response?.data?.message || t.deleteFailed || "Delete failed",
          onOk: closeModal,
          showCancel: false,
        });
      }
    } catch (e) {
      console.error("Lesson plan delete error:", e);
      setModalState({
        open: true,
        type: "error",
        title: t.failed || "Failed",
        message: t.deleteFailed || "Delete failed",
        onOk: closeModal,
        showCancel: false,
      });
    }
  };

  // when the user picks a date in the scheduled-date column filter,
  // force the toggle back to "all"
  const handleDateRangePicked = () => {
    if (weekFilter !== "all") setWeekFilter("all");
  };

  const toggleOptions = [
    { label: t.toggleAll || "All", value: "all" },
    { label: t.toggleThisWeek || "This week", value: "this_week" },
  ];

  const columns = useMemo(() => {
    const cols = [];

    if (!hideActions) {
      cols.push({
        id: "select",
        header: "",
        size: 40,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRow?.id === row.original.id}
            onChange={() => handleSelectRow(row.original)}
            className={styles.radioInput}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
      });
    }

    cols.push(
      ...buildLessonPlanColumns({
        translations,
        topicOptions,
        subTopicOptions,
        includeTopic: false, // 👈 hide Topic
        includeSubTopic: false,
        includePeriod: true,
        includeDay: true,
        includeSection: true,
        includeScheduledDate: true,
        includeStatus: true,
        onDateRangePicked: handleDateRangePicked,
      }),
    );

    if (!hideActions) {
      cols.push({
        id: "actions",
        header: t.action || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={t.view || "View"}
          >
            <FaEye />
          </button>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    translations,
    topicOptions,
    subTopicOptions,
    hideActions,
    selectedRow,
    weekFilter,
  ]);

  return (
    <>
      {/* {!hideActions && (
        <div className={styles.actionsRow}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.deleteButton}
            fullWidth={false}
            onClick={handleDelete}
          >
            {t.delete || "DELETE"}
          </CustomButton>
        </div>
      )} */}

      <div className={styles.toggleRow}>
        <CustomToggle
          options={toggleOptions}
          value={weekFilter}
          onChange={setWeekFilter}
        />
      </div>

      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        enableColumnFilters={true}
        enableGlobalFilter={false}
        hideGlobalSearch={true}
        enableExpanding={true}
        renderDetailPanel={({ row }) => <LessonPlanHistoryPanel row={row} />}
      />

      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={modalState.title}
        message={modalState.message}
        confirmText={t.ok || "OK"}
        cancelText={t.cancel || "Cancel"}
        onOk={modalState.onOk || closeModal}
        onClose={closeModal}
        showCancel={modalState.showCancel}
      />
    </>
  );
}

export default SubmittedLessonPlan;
